import { Observable } from "rxjs";
import { isEqual as deepEqual } from "lodash";

import { ProductsRepository } from "lib/repositories/index";
import { ProductFullDecorator } from "lib/decorators/index";
import st from "lib/utils/index";
import { pushRoute, replaceRoute, createAlert, removeAlert } from "modules/app/actions";
import StepAvailabilityDuck from "./StepAvailability";
import StepAccommodationSettingsDuck from "./StepAccommodationSettings";
import { _validateStep, _validateProduct } from "./validate";
import {
  STEP_AVAILABILITY,
  STEP_PRICELIST,
  STEP_PAYMENTS,
  STEP_DESCRIPTION,
  STEP_LOCATION,
  STEP_PHOTOS,
  STEP_PRICED_FIELDS,
  STEP_CUSTOMER_FIELDS,
  STEP_ADDITIONAL,
  STEP_BOOKING_SETTINGS,
  STEP_ACCOMMODATION_SETTINGS,
  STEP_INTEGRATIONS,
  STEPS,
} from "./constants";
export * from "./constants";

export const INITIALIZE_VIEW = "products/EditProductView/INITIALIZE_VIEW";
export const LOAD_PRODUCT = "products/EditProductView/LOAD_PRODUCT";
export const FETCH_PRODUCT = "products/EditProductView/FETCH_PRODUCT";
export const FETCH_PRODUCT_START = "products/EditProductView/FETCH_PRODUCT_START";
export const FETCH_PRODUCT_SUCCESS = "products/EditProductView/FETCH_PRODUCT_SUCCESS";
export const FETCH_PRODUCT_FAILURE = "products/EditProductView/FETCH_PRODUCT_FAILURE";
export const REQUESTED_STEP_SET = "products/EditProductView/REQUESTED_STEP_SET";
export const CURRENT_STEP_SET = "products/EditProductView/CURRENT_STEP_SET";
export const CURRENT_STEP_REFRESH = "products/EditProductView/CURRENT_STEP_REFRESH";
export const GENERATE_STEPS = "products/EditProductView/GENERATE_STEPS";
export const UPDATE_PRODUCT = "products/EditProductView/UPDATE_PRODUCT";
export const UPDATE_TRANSLATED_PRODUCT = "products/EditProductView/UPDATE_TRANSLATED_PRODUCT";
export const VALIDATE = "products/EditProductView/VALIDATE";
export const SET_VALIDATION_ERRORS = "products/EditProductView/SET_VALIDATION_ERRORS";
export const VALIDATE_SUCCESS = "products/EditProductView/VALIDATE_SUCCESS";
export const VALIDATE_FAILURE = "products/EditProductView/VALIDATE_FAILURE";
export const SAVE = "products/EditProductView/SAVE";
export const FETCH_SAVE_START = "products/EditProductView/FETCH_SAVE_START";
export const FETCH_SAVE_SUCCESS = "products/EditProductView/FETCH_SAVE_SUCCESS";
export const FETCH_SAVE_FAILURE = "products/EditProductView/FETCH_SAVE_FAILURE";
export const REMOVE = "products/EditProductView/REMOVE";
export const FETCH_REMOVE_START = "products/EditProductView/FETCH_REMOVE_START";
export const FETCH_REMOVE_SUCCESS = "products/EditProductView/FETCH_REMOVE_SUCCESS";
export const FETCH_REMOVE_FAILURE = "products/EditProductView/FETCH_REMOVE_FAILURE";
export const UPLOAD_PRODUCT_IMAGES = "products/EditProductView/UPLOAD_PRODUCT_IMAGES";
export const FETCH_UPLOAD_PRODUCT_IMAGES_START = "products/EditProductView/FETCH_UPLOAD_PRODUCT_IMAGES_START";
export const FETCH_UPLOAD_PRODUCT_IMAGES_SUCCESS = "products/EditProductView/FETCH_UPLOAD_PRODUCT_IMAGES_SUCCESS";
export const FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE = "products/EditProductView/FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE";
// export const FETCH_SAVE_CANCEL = "products/EditProductView/FETCH_SAVE_CANCEL"
export const SET_LOCALES = "products/EditProductView/SET_LOCALES";
export const REMOVE_LOCALE = "products/EditProductView/REMOVE_LOCALE";
export const TOGGLE_SIDEBAR = "products/EditProductView/TOGGLE_SIDEBAR";
export const RELOAD_PAYMENT_PROFILES = "products/EditProductView/RELOAD_PAYMENT_PROFILES";
export const CHANGE_EDITED_LOCALE = "products/EditProductView/CHANGE_EDITED_LOCALE";

export const stateSelector = (state) => state.products.EditProductView;

function _isProductDirty(product, initialProduct) {
  return !deepEqual(product, initialProduct);
}

const initialState = {
  initialProduct: null,
  product: null,
  steps: [],
  currentStepParams: null,
  errors: new st.form.ValidationErrors(),
  sidebarExtended: true,
  forceSidebarVisibility: true,
  fetchDone: false,
  fetchLoading: false,
  fetchFailed: false,
  dirty: false,
  valid: true,
  submitLoading: false,
  removeDone: false,
  editedLocale: null,
};

const reducers = {
  [INITIALIZE_VIEW]: (_, { product }) => ({
    ...initialState,
    initialProduct: product,
    product,
  }),

  [LOAD_PRODUCT]: (state, { initialProduct, product = initialProduct }) => ({
    ...state,
    initialProduct,
    product,
    editedLocale: state.editedLocale || initialProduct.default_locale || null,
  }),

  [FETCH_PRODUCT_START]: (state) => {
    return {
      ...state,
      fetchLoading: true,
      fetchFailed: false,
    };
  },

  [FETCH_PRODUCT_SUCCESS]: (state, { product }) => {
    return {
      ...state,
      initialProduct: product,
      product,
      fetchDone: true,
      fetchLoading: false,
      fetchFailed: false,
      dirty: false,
      editedLocale: state.editedLocale || product.default_locale || null,
    };
  },

  [FETCH_PRODUCT_FAILURE]: (state, { error }) => {
    return {
      ...state,
      fetchLoading: false,
      fetchFailed: true,
    };
  },

  [REQUESTED_STEP_SET]: (state, { stepParams }) => {
    return {
      ...state,
      requestedStepParams: stepParams,
    };
  },

  [CURRENT_STEP_SET]: (state, { stepParams: currentStepParams }) => {
    const forceSidebarVisibility = currentStepParams && currentStepParams.id === STEPS.STEP_AVAILABILITY ? false : true;

    return {
      ...state,
      currentStepParams,
      sidebarExtended: forceSidebarVisibility,
      forceSidebarVisibility,
    };
  },

  // [UPDATE_STEP_META]: (state, { stepParams, stepMeta }) => {
  //   const steps = state.steps.map(step => {
  //     if (deepEqual(step.params, stepParams)) {
  //       return {
  //         ...step,
  //         meta: { ...step.meta, ...stepMeta }
  //       }
  //     } else {
  //       return step
  //     }
  //   })
  //
  //   return {
  //     ...state,
  //     steps,
  //     meta: {
  //       ...state.meta,
  //       dirty: steps.filter(step => step.meta.dirty).length > 0,
  //       valid: steps.filter(step => step.meta.valid).length == steps.length,
  //     },
  //   }
  // },

  [GENERATE_STEPS]: (state) => {
    const locales = (state.fetchDone && state.product.available_locales) || [];
    const steps = [
      { params: { id: STEP_AVAILABILITY } },
      { params: { id: STEP_PRICELIST } },
      { params: { id: STEP_PAYMENTS } },
      { params: { id: STEP_DESCRIPTION } },
      { params: { id: STEP_LOCATION } },
      { params: { id: STEP_PHOTOS } },
      { params: { id: STEP_PRICED_FIELDS } },
      { params: { id: STEP_CUSTOMER_FIELDS } },
      { params: { id: STEP_ADDITIONAL } },
      { params: { id: STEP_BOOKING_SETTINGS } },
      { params: { id: STEP_ACCOMMODATION_SETTINGS }},
      { params: { id: STEP_INTEGRATIONS }}
    ].map((step) => {
      const prevStep = state.steps && state.steps.find((s) => deepEqual(s.params, step.params));
      return {
        ...step,
        meta: prevStep ? prevStep.meta : { dirty: false, valid: true },
      };
    });

    return {
      ...state,
      steps,
    };
  },

  [UPDATE_PRODUCT]: (state, { product }) => {
    return {
      ...state,
      product,
      dirty: _isProductDirty(product, state.initialProduct),
    };
  },

  [UPDATE_TRANSLATED_PRODUCT]: (state, { product }) => {
    return {
      ...state,
      product,
      dirty: _isProductDirty(product, state.initialProduct),
    };
  },

  [SET_VALIDATION_ERRORS]: (state, { errors }) => {
    const steps = state.steps.map((step) => ({
      ...step,
      meta: {
        ...step.meta,
        valid: _validateStep({
          step,
          errors,
          product: state.product,
          editedLocale: state.editedLocale,
        }),
      },
    }));

    return {
      ...state,
      steps,
      errors,
      valid: errors.length === 0,
    };
  },

  [FETCH_SAVE_START]: (state) => {
    return {
      ...state,
      submitLoading: true,
    };
  },

  [FETCH_SAVE_SUCCESS]: (state, { product }) => {
    return {
      ...state,
      initialProduct: product,
      product,
      submitLoading: false,
      dirty: false,
    };
  },

  [FETCH_SAVE_FAILURE]: (state, { error }) => {
    return {
      ...state,
      submitLoading: false,
    };
  },

  [FETCH_UPLOAD_PRODUCT_IMAGES_START]: (state) => {
    return {
      ...state,
      submitLoading: true,
    };
  },

  [FETCH_UPLOAD_PRODUCT_IMAGES_SUCCESS]: (state, { product }) => {
    const nextInitialProduct = new ProductFullDecorator({
      ...state.initialProduct,
      product_images: product.product_images,
    });
    const nextProduct = new ProductFullDecorator({
      ...state.product,
      lock_version: state.product.lock_version,
      product_images: product.product_images,
    });

    return {
      ...state,
      initialProduct: nextInitialProduct,
      product: nextProduct,
      dirty: _isProductDirty(nextProduct, nextInitialProduct),
      submitLoading: false,
    };
  },

  [FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE]: (state, { error }) => {
    return {
      ...state,
      submitLoading: false,
    };
  },

  [FETCH_REMOVE_SUCCESS]: (state) => ({
    ...state,
    removeDone: true,
  }),

  [TOGGLE_SIDEBAR]: (state, { toggle }) => {
    const sidebarExtended = toggle === null || toggle === undefined ? !state.sidebarExtended : !!toggle;
    return {
      ...state,
      sidebarExtended,
    };
  },

  [CHANGE_EDITED_LOCALE]: (state, { editedLocale }) => ({
    ...state,
    editedLocale,
  }),
};

const epics = [
  (actions$) => actions$.ofType(INITIALIZE_VIEW).mapTo({ type: FETCH_PRODUCT }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(FETCH_PRODUCT).flatMap(() => {
      const { initialProduct } = stateSelector(getState());
      return Observable.concat(
        Observable.of({ type: FETCH_PRODUCT_START }),
        ProductsRepository.find(initialProduct.id)
          .map((product) => {
            return { type: FETCH_PRODUCT_SUCCESS, product };
          })
          .catch((error) => {
            st.api.handleError(error, { getState, dispatch });
            return Observable.of({ type: FETCH_PRODUCT_FAILURE, error });
          })
      );
    }),

  (actions$) =>
    actions$.ofType(FETCH_PRODUCT_SUCCESS).mapTo({
      type: SET_VALIDATION_ERRORS,
      errors: new st.form.ValidationErrors(),
    }),

  // GENERATE_STEPS when needed
  (actions$, { getState }) =>
    actions$
      .ofType(INITIALIZE_VIEW)
      .merge(actions$.ofType(FETCH_PRODUCT_SUCCESS))
      .merge(actions$.ofType(UPDATE_PRODUCT))
      .map(() => {
        const { fetchDone, product } = stateSelector(getState());
        return fetchDone && product && product.available_locales.join("#");
      })
      .distinctUntilChanged()
      .mapTo({ type: GENERATE_STEPS }),

  // set currentStepParams, responding to product and external changes of current step
  (actions$, { getState }) =>
    actions$
      .ofType(FETCH_PRODUCT_SUCCESS)
      .merge(actions$.ofType(CURRENT_STEP_REFRESH))
      .combineLatest(actions$.ofType(REQUESTED_STEP_SET), (_, { stepParams }) => {
        const { fetchDone, product, requestedStepParams } = stateSelector(getState());
        let nextStepParams = { ...stepParams };

        if (fetchDone) {
          if (!nextStepParams.id) {
            nextStepParams.id = STEP_DESCRIPTION;
          }

          // Disallow STEP_AVAILABILITY for no_booking products
          if (product.booking_type === "no_booking" && nextStepParams.id === STEP_AVAILABILITY) {
            nextStepParams = { id: STEP_PRICELIST };
          }

          // Disallow steps that are disabled for coupon products
          if (
            product.product_type === "coupon" &&
            [STEP_PRICED_FIELDS, STEP_ADDITIONAL].indexOf(nextStepParams.id) !== -1
          ) {
            nextStepParams = { id: STEP_DESCRIPTION };
          }

          // Disallow STEP_ACCOMMODATION_SETTINGS for products that are not accommodations.
          if (product.product_type !== "accommodation" && nextStepParams.id === STEP_ACCOMMODATION_SETTINGS) {
            nextStepParams = { id: STEP_DESCRIPTION };
          }
        }

        if (!deepEqual(requestedStepParams, nextStepParams)) {
          const newUrl = buildProductUrl(getState().auth.company.model.id, product, { step: nextStepParams.id });
          return replaceRoute(newUrl);
        } else {
          return { type: CURRENT_STEP_SET, stepParams: nextStepParams };
        }
      }),

  (actions$, { getState }) =>
    actions$
      .ofType(CURRENT_STEP_SET)
      .filter(({ stepParams }) => stepParams.id === STEP_AVAILABILITY)
      .map(() => {
        const { initialProduct } = stateSelector(getState());
        return loadProduct(initialProduct);
      }),

  // SAVE
  (actions$, { dispatch, getState }) =>
    actions$
      .ofType(SAVE)
      .switchMap(() => {
        setTimeout(() => {
          dispatch(validate({ submitValidate: true }));
        });
        return Observable.race(actions$.ofType(VALIDATE_FAILURE), actions$.ofType(VALIDATE_SUCCESS)).take(1);
      })
      .filter(({ type }) => type === VALIDATE_SUCCESS)
      .flatMap(() => {
        const { product, initialProduct } = stateSelector(getState());
        return Observable.concat(
          Observable.of({ type: FETCH_SAVE_START }),
          Observable.of(removeAlert({ uuid: FETCH_SAVE_FAILURE })),
          ProductsRepository.update(
            {
              ...product,
              product_variants: product.product_variants.map((pv) => ({
                ...pv,
                product_pricelist_items: pv.current_product_pricelist_items,
              })),
            },
            initialProduct
          )
            .flatMap((product) => {
              return Observable.concat(
                Observable.of({ type: FETCH_SAVE_SUCCESS, product }),
                Observable.of(
                  createAlert({
                    type: "success",
                    content: I18n.t("papp.products.EditProductView.common.save_success_message"),
                  })
                )
              );
            })
            .catch((error) => {
              st.api.handleError(error, { getState, dispatch });
              // const errors = new st.form.ValidationErrors([ ["base", st.api.findResponseErrorMessage(error)] ])
              return Observable.concat(
                // Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
                Observable.of({ type: FETCH_SAVE_FAILURE, error }),
                Observable.of(
                  createAlert({
                    uuid: FETCH_SAVE_FAILURE,
                    type: "error",
                    content: st.api.findResponseErrorMessage(error),
                    timeout: false,
                  })
                )
              );
            })
        );
      }),

  // REMOVE
  (actions$, { dispatch, getState }) =>
    actions$.ofType(REMOVE).flatMap(() => {
      const { product } = stateSelector(getState());
      const currentCompanyId = getState().auth.company.model.id;
      if (!product.id) {
        return Observable.empty();
      }
      return Observable.concat(
        Observable.of({ type: FETCH_REMOVE_START }),
        Observable.of(removeAlert({ uuid: FETCH_REMOVE_FAILURE })),
        ProductsRepository.delete(product.id)
          .flatMap(() => {
            return Observable.concat(
              Observable.of({ type: FETCH_REMOVE_SUCCESS }),
              Observable.of(
                createAlert({
                  content: I18n.t("papp.products.EditProductView.common.remove_success_message"),
                })
              ),
              Observable.of(pushRoute(`/c/${currentCompanyId}/products`))
            );
          })
          .catch((error) => {
            st.api.handleError(error, { getState, dispatch });
            // const errors = new st.form.ValidationErrors([ ["base", st.api.findResponseErrorMessage(error)] ])
            return Observable.concat(
              // Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
              Observable.of({ type: FETCH_REMOVE_FAILURE, error }),
              Observable.of(
                createAlert({
                  uuid: FETCH_REMOVE_FAILURE,
                  type: "error",
                  content: st.api.findResponseErrorMessage(error),
                  timeout: false,
                })
              )
            );
          })
      );
    }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(UPLOAD_PRODUCT_IMAGES).flatMap(({ attachments }) => {
      const { product } = stateSelector(getState());
      return Observable.concat(
        Observable.of({ type: FETCH_UPLOAD_PRODUCT_IMAGES_START }),
        Observable.of(removeAlert({ uuid: FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE })),
        ProductsRepository.update(
          {
            id: product.id,
            product_images: attachments,
          },
          undefined,
          { useFormData: true }
        )
          .flatMap((product) =>
            Observable.concat(
              Observable.of({
                type: FETCH_UPLOAD_PRODUCT_IMAGES_SUCCESS,
                product,
              }),
              Observable.of(
                createAlert({
                  type: "success",
                  content: I18n.t("papp.products.EditProductView.common.add_photos_success_message"),
                })
              )
            )
          )
          .catch((error) => {
            st.api.handleError(error, { getState, dispatch });
            // const errors = new st.form.ValidationErrors([ ["base", st.api.findResponseErrorMessage(error)] ])
            return Observable.concat(
              // Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
              Observable.of({
                type: FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE,
                error,
              }),
              Observable.of(
                createAlert({
                  uuid: FETCH_UPLOAD_PRODUCT_IMAGES_FAILURE,
                  type: "error",
                  content: st.api.findResponseErrorMessage(error),
                  timeout: false,
                })
              )
            );
          })
      );
    }),

  (actions$, { getState }) =>
    actions$.ofType(SET_LOCALES).flatMap(({ locales }) => {
      const { product, editedLocale } = stateSelector(getState());
      const newLocales = locales.filter((l) => !product.available_locales.includes(l));
      const nextLocale =
        newLocales.length > 0 ? newLocales[0] : locales.find((l) => l === editedLocale) ? editedLocale : locales[0];
      const nextUrl =
        nextLocale !== editedLocale
          ? buildProductUrl(getState().auth.company.model.id, product, {
              step: STEP_DESCRIPTION,
            })
          : null;
      const nextProduct = {
        ...product,
        translations: product.translations
          .filter((t) => locales.includes(t.locale))
          .concat(
            newLocales.map((locale) => ({
              // ...product.translation,
              name: product.translation.name,
              id: undefined,
              uuid: st.generateUuid(),
              type: "Product::Translation",
              locale,
            }))
          ),
        product_variants: product.product_variants.map((pv) => ({
          ...pv,
          translations: pv.translations
            .filter((t) => locales.includes(t.locale))
            .concat(
              newLocales.map((locale) => ({
                // ...pv.translation,
                name: pv.translation.name,
                id: undefined,
                uuid: st.generateUuid(),
                type: "ProductVariant::Translation",
                locale,
              }))
            ),
        })),
      };
      return Observable.concat(
        Observable.of(updateProduct(nextProduct)),
        nextLocale !== editedLocale ? Observable.of(changeEditedLocale(nextLocale)) : Observable.empty(),
        nextUrl ? Observable.of(pushRoute(nextUrl)) : Observable.empty()
      );
    }),

  (actions$, { getState }) =>
    actions$.ofType(REMOVE_LOCALE).flatMap(({ locale }) => {
      const { product, editedLocale } = stateSelector(getState());
      const nextProduct = {
        ...product,
        translations: product.translations.filter((t) => t.locale !== locale),
        product_variants: product.product_variants.map((pv) => ({
          ...pv,
          translations: pv.translations.filter((t) => t.locale !== locale),
        })),
      };
      const nextLocale =
        locale === editedLocale
          ? product.available_locales.find((l) => l !== editedLocale) || I18n.locale
          : editedLocale;
      return Observable.concat(
        Observable.of(updateProduct(nextProduct)),
        Observable.of({ type: CURRENT_STEP_REFRESH }),
        nextLocale !== editedLocale ? Observable.of(changeEditedLocale(nextLocale)) : Observable.empty()
      );
    }),

  (actions$, { getState, dispatch }) =>
    actions$.ofType(RELOAD_PAYMENT_PROFILES).flatMap(() => {
      const { initialProduct } = stateSelector(getState());
      if (!initialProduct || !initialProduct.id) {
        return Observable.empty();
      }
      return ProductsRepository.find(initialProduct.id)
        .flatMap((persistedProduct) => {
          const { product: dirtyProduct } = stateSelector(getState());
          if (!dirtyProduct || dirtyProduct.id !== persistedProduct.id) {
            return Observable.empty();
          }

          const nextProduct = new ProductFullDecorator({
            ...dirtyProduct,
            lock_version: persistedProduct.lock_version,
            product_variants: dirtyProduct.product_variants.map((dirtyPv) => {
              const persistedPv = persistedProduct.product_variants.find((pv) => pv.id === dirtyPv.id) || {
                payment_profile_product_attachments: [],
              };
              const persistedAttachmentIds = persistedPv.payment_profile_product_attachments.map((at) => at.id);
              const dirtyAttachmentIds = dirtyPv.payment_profile_product_attachments.map((at) => at.id);
              return {
                ...dirtyPv,
                payment_profile_product_attachments: persistedPv.payment_profile_product_attachments
                  .filter((at) => !dirtyAttachmentIds.includes(at.id))
                  .concat(
                    dirtyPv.payment_profile_product_attachments.filter((at) => persistedAttachmentIds.includes(at.id))
                  ),
              };
            }),
          });

          return Observable.of(loadProduct(persistedProduct, nextProduct));
        })
        .catch((error) => {
          st.api.handleError(error, { getState, dispatch });
          return Observable.of({ type: FETCH_PRODUCT_FAILURE, error });
        });
    }),

  (actions$, { getState }) =>
    actions$.ofType(VALIDATE).flatMap(({ submitValidate }) => {
      const { product } = stateSelector(getState());
      const errors = _validateProduct(product, { submitValidate });
      return Observable.concat(
        Observable.of({ type: SET_VALIDATION_ERRORS, errors }),
        Observable.of({
          type: errors.length > 0 ? VALIDATE_FAILURE : VALIDATE_SUCCESS,
        })
      );
    }),
];

export const buildProductUrl = (currentCompanyId, product, { step } = {}) => ({
  pathname: `/c/${currentCompanyId}/products/${product.id}/edit`,
  query: {
    step,
  },
});

export function initializeView(product) {
  return { type: INITIALIZE_VIEW, product };
}

export function loadProduct(initialProduct, product) {
  return { type: LOAD_PRODUCT, initialProduct, product };
}

export function fetchProduct() {
  return { type: FETCH_PRODUCT };
}

export function requestStep(stepParams) {
  return { type: REQUESTED_STEP_SET, stepParams };
}

export function updateProduct(productData) {
  return {
    type: UPDATE_PRODUCT,
    product: new ProductFullDecorator(productData),
  };
}

export function updateTranslatedProduct(product) {
  return {
    type: UPDATE_TRANSLATED_PRODUCT,
    product: new ProductFullDecorator(product),
  };
}

export function uploadProductImages(attachments) {
  return { type: UPLOAD_PRODUCT_IMAGES, attachments };
}

export function validate({ submitValidate = false } = {}) {
  return { type: VALIDATE, submitValidate };
}

export function save() {
  return { type: SAVE };
}

export function remove() {
  return { type: REMOVE };
}

export function setLocales(locales) {
  return { type: SET_LOCALES, locales };
}

export function removeLocale(locale) {
  return { type: REMOVE_LOCALE, locale };
}

export function toggleSidebar(toggle) {
  return { type: TOGGLE_SIDEBAR, toggle };
}

export function reloadPaymentProfiles() {
  return { type: RELOAD_PAYMENT_PROFILES };
}

export function changeEditedLocale(editedLocale) {
  return { type: CHANGE_EDITED_LOCALE, editedLocale };
}

export default st.duck.createDuck(initialState, reducers, epics, {
  StepAvailability: StepAvailabilityDuck,
  StepAccommodationSettings: StepAccommodationSettingsDuck,
});



// WEBPACK FOOTER //
// ./src/modules/products/EditProductView/index.js